import React from 'react';
import { X, ArrowUpRight, ArrowDownLeft, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Transaction } from '../App';

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
}

export function TransactionDetailsModal({ isOpen, onClose, transaction }: TransactionDetailsModalProps) {
  if (!isOpen || !transaction) return null;

  const copyTransactionId = async () => {
    try {
      await navigator.clipboard.writeText(`TXN${transaction.id.toString().padStart(8, '0')}`);
      toast.success('Transaction ID copied!');
    } catch (error) {
      toast.error('Failed to copy transaction ID');
    }
  };

  const openExplorer = () => {
    // 실제 블록체인 익스플로러로 연결할 예정
    toast.info('Opening blockchain explorer...');
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-2xl max-w-sm w-full max-h-[90vh] overflow-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="font-bold text-lg text-foreground">Transaction Details</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status */}
          <div className="text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              transaction.amount > 0 
                ? 'bg-green-500/20 border-2 border-green-500/30' 
                : 'bg-red-500/20 border-2 border-red-500/30'
            }`}>
              {transaction.amount > 0 ? (
                <ArrowDownLeft className="w-8 h-8 text-green-600" />
              ) : (
                <ArrowUpRight className="w-8 h-8 text-red-600" />
              )}
            </div>
            <h3 className="font-bold text-xl text-foreground mb-2">{transaction.type}</h3>
            <div className={`text-3xl font-bold mb-1 ${
              transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {transaction.amount > 0 ? '+' : ''}{parseFloat(transaction.amount).toFixed(2)} VTTN
            </div>
            <p className="text-muted-foreground text-sm">
              ≈ ${Math.abs(transaction.amount * 0.85).toFixed(2)} USD
            </p>
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div className="bg-muted rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-muted-foreground text-sm">Transaction ID</span>
                <button
                  onClick={copyTransactionId}
                  className="text-primary hover:text-primary/80 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="font-mono text-sm text-foreground">
                TXN{transaction.id.toString().padStart(8, '0')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted rounded-xl p-4">
                <p className="text-muted-foreground text-sm mb-1">Date</p>
                <p className="font-medium text-foreground">
                  {new Date(transaction.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div className="bg-muted rounded-xl p-4">
                <p className="text-muted-foreground text-sm mb-1">Time</p>
                <p className="font-medium text-foreground">
                  {new Date(transaction.createdAt).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            <div className="bg-muted rounded-xl p-4">
              <p className="text-muted-foreground text-sm mb-1">Status</p>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="font-medium text-green-600">Completed</span>
              </div>
            </div>

            <div className="bg-muted rounded-xl p-4">
              <p className="text-muted-foreground text-sm mb-1">Network</p>
              <p className="font-medium text-foreground">Ethereum Mainnet</p>
            </div>

            {/* Gas Fee */}
            <div className="bg-muted rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Network Fee</p>
                  <p className="font-medium text-foreground">0.0024 ETH</p>
                </div>
                <div className="text-right">
                  <p className="text-muted-foreground text-sm">≈ $8.50</p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={openExplorer}
              className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View on Explorer</span>
            </button>
            
            <button
              onClick={onClose}
              className="w-full bg-muted text-foreground py-3 rounded-xl font-medium hover:bg-accent transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}